/**
 * AURA Color Picker Control
 */

export interface ColorPickerOptions {
  id: string;
  label: string;
  selectedColor?: string | null;
  colors?: string[];
  onChange?: (color: string | null) => void;
}

const DEFAULT_COLORS = [
  '#1e3a8a', // Blue
  '#7c3aed', // Purple
  '#dc2626', // Red
  '#ea580c', // Orange
  '#16a34a', // Green
  '#0891b2', // Teal
  '#ffffff', // White
  '#000000', // Black
];

const COLOR_PICKER_STYLES = `
  .aura-color-picker {
    padding: var(--aura-spacing-sm) 0;
  }

  .aura-color-picker__label {
    display: block;
    font-size: var(--aura-font-size-md);
    color: var(--aura-text);
    margin-bottom: var(--aura-spacing-sm);
  }

  .aura-color-picker__colors {
    display: flex;
    flex-wrap: wrap;
    gap: var(--aura-spacing-sm);
  }

  .aura-color-picker__color {
    width: 32px;
    height: 32px;
    border-radius: var(--aura-radius-full);
    border: 2px solid var(--aura-border);
    cursor: pointer;
    transition: transform var(--aura-transition-fast), box-shadow var(--aura-transition-fast);
  }

  .aura-color-picker__color:hover {
    transform: scale(1.1);
  }

  .aura-color-picker__color--selected {
    box-shadow: 0 0 0 3px var(--aura-primary);
  }

  .aura-color-picker__color:focus-visible {
    outline: 2px solid var(--aura-border-focus);
    outline-offset: 2px;
  }

  .aura-color-picker__cancel {
    font-size: var(--aura-font-size-sm);
    color: var(--aura-text-muted);
    padding: var(--aura-spacing-xs) var(--aura-spacing-sm);
    border-radius: var(--aura-radius-sm);
    transition: background-color var(--aura-transition-fast);
  }

  .aura-color-picker__cancel:hover {
    background: var(--aura-bg-hover);
    color: var(--aura-text);
  }
`;

export function createColorPicker(options: ColorPickerOptions): { element: HTMLElement; styles: string; setColor: (c: string | null) => void } {
  const colors = options.colors || DEFAULT_COLORS;
  let selectedColor = options.selectedColor || null;

  const container = document.createElement('div');
  container.className = 'aura-color-picker';

  const label = document.createElement('span');
  label.className = 'aura-color-picker__label';
  label.id = `${options.id}-label`;
  label.textContent = options.label;

  const colorsContainer = document.createElement('div');
  colorsContainer.className = 'aura-color-picker__colors';
  colorsContainer.setAttribute('role', 'radiogroup');
  colorsContainer.setAttribute('aria-labelledby', `${options.id}-label`);

  const colorButtons: HTMLButtonElement[] = [];

  colors.forEach((color, index) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'aura-color-picker__color';
    btn.style.backgroundColor = color;
    btn.setAttribute('role', 'radio');
    btn.setAttribute('aria-checked', String(color === selectedColor));
    btn.setAttribute('aria-label', `Color ${index + 1}: ${color}`);
    btn.setAttribute('data-color', color);

    if (color === selectedColor) {
      btn.classList.add('aura-color-picker__color--selected');
    }

    btn.addEventListener('click', () => {
      selectColor(color);
    });

    colorButtons.push(btn);
    colorsContainer.appendChild(btn);
  });

  const resetBtn = document.createElement('button');
  resetBtn.type = 'button';
  resetBtn.className = 'aura-color-picker__cancel';
  resetBtn.textContent = 'Reset';
  resetBtn.setAttribute('aria-label', 'Reset to default color');
  resetBtn.addEventListener('click', () => {
    selectColor(null);
  });
  colorsContainer.appendChild(resetBtn);

  container.appendChild(label);
  container.appendChild(colorsContainer);

  const updateVisuals = (color: string | null) => {
    selectedColor = color;
    colorButtons.forEach((btn) => {
      const btnColor = btn.getAttribute('data-color');
      const isSelected = btnColor === color;
      btn.classList.toggle('aura-color-picker__color--selected', isSelected);
      btn.setAttribute('aria-checked', String(isSelected));
    });
  };

  const selectColor = (color: string | null) => {
    updateVisuals(color);
    options.onChange?.(color);
  };

  return {
    element: container,
    styles: COLOR_PICKER_STYLES,
    setColor: updateVisuals,
  };
}

export { COLOR_PICKER_STYLES };
